import { useState, useRef, useEffect } from 'react';
import './MaskEditor.css';

function MaskEditor({ imageBase64, onMaskChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [brushColor, setBrushColor] = useState('#000000');
  const [ctx, setCtx] = useState(null);

  // Initialize canvas with image
  useEffect(() => {
    if (!imageBase64 || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      context.fillStyle = '#000000';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, 0, 0);
      setCtx(context);
    };
    img.src = imageBase64;
  }, [imageBase64]);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Handle touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const pos = getMousePos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing || !ctx) return;

    const pos = getMousePos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Notify parent of mask change
    if (canvasRef.current) {
      onMaskChange(canvasRef.current.toDataURL());
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (ctx) ctx.closePath();
  };

  const clearMask = () => {
    if (!ctx || !imageBase64) return;
    
    const canvas = canvasRef.current;
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      onMaskChange(canvas.toDataURL());
    };
    img.src = imageBase64;
  };

  const fillMask = () => {
    if (!ctx) return;
    
    const canvas = canvasRef.current;
    ctx.fillStyle = brushColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onMaskChange(canvas.toDataURL());
  };

  if (!imageBase64) {
    return null;
  }

  return (
    <div className="mask-editor-container">
      <div className="mask-editor-controls mb-3">
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label fw-bold small">Brush Size</label>
            <input
              type="range"
              className="form-range"
              min="5"
              max="100"
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
            />
            <small className="text-muted">{brushSize}px</small>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-bold small">Mask Color</label>
            <div className="d-flex gap-2">
              <input
                type="color"
                className="form-control form-control-color"
                value={brushColor}
                onChange={(e) => setBrushColor(e.target.value)}
                style={{ width: '50px' }}
              />
              <small className="text-muted align-self-center">{brushColor}</small>
            </div>
          </div>

          <div className="col-md-4 d-flex gap-2 align-items-end">
            <button
              className="btn btn-sm btn-outline-secondary flex-grow-1"
              onClick={clearMask}
            >
              Clear
            </button>
            <button
              className="btn btn-sm btn-outline-secondary flex-grow-1"
              onClick={fillMask}
            >
              Fill
            </button>
          </div>
        </div>
      </div>

      <div className="mask-editor-canvas-wrapper">
        <canvas
          ref={canvasRef}
          className="mask-editor-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
          style={{ cursor: 'crosshair', display: 'block', touchAction: 'none' }}
        />
      </div>

      <small className="text-muted d-block mt-2">
        Draw on the canvas to create a mask. White areas will be inpainted. Black areas will remain unchanged.
      </small>
    </div>
  );
}

export default MaskEditor;
